import React, { useState, useEffect } from 'react';
import './App.css';
import { LanguageProvider, useLanguage } from './LanguageContext';
import LoadingAnimation from './components/LoadingAnimation';
import ShinyCursor from './components/ShinyCursor';
import './components/LoadingAnimation.css';
import './components/ShinyCursor.css';

// Static data that doesn't change between languages
const personalInfo = {
  name: "Berk Pekatik",
  email: "berkcanpekatik@gmail.com",
  phone: "+90 541 201 ** **",
  profileImage: "https://berkpekatik.com/img/vesika.jpg"
};

const skills = [
  // .NET & C#
  "C#", "ASP.NET MVC", "ASP.NET Core", "Entity Framework", "EF Core", "LINQ",
  // PHP
  "PHP",
  // Web API
  "REST API", "GraphQL", "JWT", "OAuth2", "Swagger", "Postman",
  // Database
  "SQL", "MySQL", "MSSQL", "Stored Procedures",
  // DevOps
  "Docker", "CI/CD", "GitHub Actions", "Git",
  // Architecture
  "SOLID", "OOP", "Repository Pattern", "N-Tier Architecture",
  // Frontend (Support Level)
  "React"
];

// Language Toggle Component
const LanguageToggle = () => {
  const { currentLanguage, toggleLanguage } = useLanguage();
  
  return (
    <button className="language-toggle" onClick={toggleLanguage}>
      <i className="fas fa-language"></i>
      {currentLanguage === 'en' ? 'TR' : 'EN'}
    </button>
  );
};

// Components
const Header = () => {
  const { t } = useLanguage();
  
  return (
    <div className="header">
      <div className="header-content">
        <div className="profile-image-container">
          <img src={personalInfo.profileImage} alt="Profile" />
        </div>
        <div className="header-info">
          <h1>{t.name}</h1>
          <div className="job-title">{t.title}</div>
          <div className="contact-info">
            <div className="contact-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>{t.location}</span>
            </div>
            <div className="contact-item">
              <i className="fas fa-envelope"></i>
              <a href={`mailto:${personalInfo.email}`}>{personalInfo.email}</a>
            </div>
            <div className="contact-item">
              <i className="fas fa-phone"></i>
              <span>{personalInfo.phone}</span>
            </div>
          </div>
        </div>
      </div>
      <LanguageToggle />
    </div>
  );
};

const ProfileSection = () => {
  const { t } = useLanguage();
  
  return (
    <div className="section">
      <div className="section-title">{t.sections.profile}</div>
      <p className="profile-text">{t.profile}</p>
    </div>
  );
};

const SkillsSection = () => {
  const { t } = useLanguage();
  
  return (
    <div className="section">
      <div className="section-title">{t.sections.skills}</div>
      <div className="skills-grid">
        {skills.map((skill, index) => (
          <div key={index} className="skill-tag">{skill}</div>
        ))}
      </div>
    </div>
  );
};

const ProjectsSection = () => {
  const { t } = useLanguage();
  
  return (
    <div className="section">
      <div className="section-title">{t.sections.projects}</div>
      {t.projects.map((project, index) => (
        <div key={index} className="project-item">
          <div className="project-title">{project.title}</div>
          <div className="project-desc">{project.description}</div>
          <div className="tech-stack">{t.techStackPrefix} {project.techStack}</div>
          {/* Static links for projects */}
          {index === 1 && (
            <a href="https://safetag.com.tr" className="project-link" target="_blank" rel="noopener noreferrer">
              safetag.com.tr
            </a>
          )}
          {index === 2 && (
            <a href="https://s2c.services" className="project-link" target="_blank" rel="noopener noreferrer">
              s2c.services
            </a>
          )}
          {index === 3 && (
            <a href="https://here-app.com" className="project-link" target="_blank" rel="noopener noreferrer">
              here-app.com
            </a>
          )}
          {index === 4 && (
            <a href="https://grupye.com" className="project-link" target="_blank" rel="noopener noreferrer">
              grupye.com
            </a>
          )}
          {index === 5 && (
            <a href="http://www.medeniyetz.com/" className="project-link" target="_blank" rel="noopener noreferrer">
              medeniyetz.com
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

const ExperienceSection = () => {
  const { t } = useLanguage();
  
  return (
    <div className="right-section">
      <div className="right-section-title">
        <i className="fas fa-briefcase"></i>
        {t.sections.experience}
      </div>
      {t.experience.map((exp, index) => (
        <div key={index} className="experience-item">
          <div className="company-name">{exp.company}</div>
          <div>{exp.position}</div>
          <div className="job-period">{exp.period}</div>
        </div>
      ))}
    </div>
  );
};

const EducationSection = () => {
  const { t } = useLanguage();
  
  return (
    <div className="right-section">
      <div className="right-section-title">
        <i className="fas fa-graduation-cap"></i>
        {t.sections.education}
      </div>
      {t.education.map((edu, index) => (
        <div key={index} className="education-item">
          <div className="company-name">{edu.institution}</div>
          <div>{edu.program}</div>
          {edu.details && <div className="job-period">{edu.details}</div>}
        </div>
      ))}
    </div>
  );
};

const LanguagesSection = () => {
  const { t } = useLanguage();
  
  return (
    <div className="right-section">
      <div className="right-section-title">
        <i className="fas fa-globe"></i>
        {t.sections.languages}
      </div>
      {t.languageSkills.map((lang, index) => (
        <div key={index} className="language-item">
          <strong>{lang.language}:</strong> {lang.level}
        </div>
      ))}
    </div>
  );
};

const OtherSection = () => {
  const { t } = useLanguage();
  
  return (
    <div className="right-section">
      <div className="right-section-title">
        <i className="fas fa-certificate"></i>
        {t.sections.other}
      </div>
      <div className="certification">
        <strong>{t.other.certifications}</strong><br />
        {t.other.certification}
      </div>
      <div className="hobbies">
        <strong>{t.other.hobbies}</strong><br />
        {t.other.hobby}
      </div>
    </div>
  );
};

// Main App Component Content
const AppContent = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time for the CV data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500); // 2.5 seconds loading time

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <LoadingAnimation isLoading={isLoading} />
      <div className={`app-content ${isLoading ? 'loading' : ''}`}>
        <div className="container">
          <Header />
          <div className="main-content">
            <div className="left-column">
              <ProfileSection />
              <SkillsSection />
              <ProjectsSection />
            </div>
            <div className="right-column">
              <ExperienceSection />
              <EducationSection />
              <LanguagesSection />
              <OtherSection />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Main App Component with Language Provider
function App() {
  return (
    <LanguageProvider>
      <ShinyCursor />
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
